<template>
  <div>
    <label class="label" @click="onGenerateVin($event)">
      Vin No
      <b-icon pack="fas" :icon="requiredIcon" class="fa-ss"></b-icon>
      <i v-if="showWarning" class="fas fa-exclamation-triangle is-not-valid fa-lg fa-throb"></i>
      <span class="is-not-valid" v-if="showWarning">INVALID</span>
    </label>
    <div class="field has-addons is-marginless">
      <div class="control">
        <input name="vinNo" :data-vv-scope="scope" data-vv-as="vin no" v-validate="{'required': vinRequired, 'verify_vin': vinRequired, 'min': vinRequired ? 11 : 0 }"
        style="width:175px" :class="{'input': true, 'is-danger': errors.has(validationName), 'is-valid': isValid, 'is-not-valid': !isValid }" type="text"
        placeholder="FF29387492" maxlength="17" v-model="selectedVinNo" v-on:blur="onVinBlur" :tabindex="tabIndex == null ? '' : tabIndex" :disabled="disabled">
        <span v-show="errors.has(validationName)" class="help is-danger"><i v-show="errors.has(validationName)" class="fas fa-exclamation-triangle"></i> {{ errors.first(validationName) }}</span>
      </div>
      <div class="control">
        <button class="button" :disabled="disabled" :class="{'is-loading': decodingVin}" @click.prevent="onVinDecodeClick" v-tooltip="{content: 'Click to run a vin-decode. This function is disabled if previously decoded or if year, make and model is already set. CTRL + Click will force a new vin-decode run', delay: {show: 500, hide: 100}}">
        <b-icon pack="fas" icon="barcode"></b-icon>
        </button>
      </div>
    </div>
    <p></p>
    <carfax-payment-required-control :key="currentUser.id" :vinNo="selectedVinNo" :showPaymentRequiredModal="isPaymentRequiredForReportActive" v-on:on-carfax-purchase-requested="onCarfaxPurchaseRequested" v-on:on-carfax-purchase-failed="onCarfaxPurchaseFailed" v-on:on-new-carfax-purchased="onCarfaxPurchased" v-on:on-carfax-login-required="onCarfaxLoginRequired" :useModalPortal="true" v-on:carfax-payment-modal-closed="onCarfaxPaymentModalClosed"></carfax-payment-required-control>
  </div>
</template>

<script>

import eventBus from '@/eventBus'
import inventoryService from '@/services/inventoryService'
import appraisalService from '@/services/appraisalService'
import { findCatalogueModel, decodedModelNeedsChoice } from '@/utils/carfax_make_model_resolution'
import { createNamespacedHelpers } from 'vuex'
const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapDealerState = createNamespacedHelpers('dealer').mapState
import CarFaxPaymentRequiredControl from '@/components/generic/CarFaxPaymentRequiredControl'

export default {
  inject: {
    $validator: '$validator'
  },
  name: 'VinNoSelector',
  components: {
    'carfax-payment-required-control': CarFaxPaymentRequiredControl
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    vehicleId: {
      type: Number,
      default: 0
    },
    productionYear: {
      type: String,
      default: ''
    },
    make: {
      type: String,
      default: ''
    },
    model: {
      type: String,
      default: ''
    },
    vinRequired: {
      type: Boolean,
      default: true
    },
    tabIndex: {
      type: Number,
      default: null
    },
    scope: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    getAutoCheck: {
      type: Boolean,
      default: true
    },
    ppaClosedCheck: {
      type: Boolean,
      default: false
    },
    useValidator: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      decodingVin: false,
      selectedVinNo: this.value,
      selectedMakeModel: {
        decoded: false,
        vin: '',
        makeId: null,
        modelId: null,
        selectedMake: null,
        selectedModel: null,
        titleStatus: null,
        isDamaged: false,
        titleFiles: [],
        damageFiles: [],
        damageNote: '',
        productionYear: null,
        trim: '',
        style: '',
        exteriorColor: '',
        interiorColor: '',
        customMakeModel: '',
        locateType: null,
        altExteriorColors: [],
        altInteriorColors: [],
        tags: [],
        notes: '',
        lastInventory: null,
        accessoryStatusHistory: [],
        damageHistory: [],
        serviceHistory: []
      },
      carfaxMakeModelName: '',
      carfaxCustomMakeModelName: '',
      carfaxModelNameDifferent: true,
      isPaymentRequiredForReportActive: false
    }
  },
  computed: {
    ...mapVehicleMakeGetters(['allVehicleMakes']),
    ...mapUserGetters(['hasFeatureAccess', 'currentUser']),
    ...mapDealerState(['currentDealer']),
    showWarning: function () {
      if (!this.vinRequired) return false
      return this.errors.has(this.validationName) || this.isValid === false
    },
    makes: function () {
      return this.allVehicleMakes
    },
    requiredIcon: function () {
      return this.vinRequired ? 'star' : ''
    },
    isValid: function () {
      return this.selectedVinNo && this.selectedVinNo.length === 17
    },
    validationName: function () {
      return this.scope ? this.scope + '.vinNo' : 'vinNo'
    },
    hasCarfaxTesterRole: function () {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.find((x) => { return x.name.toLowerCase().includes('carfax tester') }) !== undefined
      }
      return true
    }
  },
  methods: {
    onVinDecodeClick: function (ev) {
      this.onDecodeVin(false, false, ev.ctrlKey)
    },
    onVinBlur: function () {
      if (this.productionYear && this.make && this.model) return
      this.onDecodeVin(false, false, false)
    },
    resetDefaults: function () {
      this.selectedMakeModel = {
        decoded: false,
        dcResult: null,
        vin: '',
        makeId: -1,
        modelId: -1,
        selectedMake: null,
        selectedModel: null,
        productionYear: null,
        trim: '',
        style: '',
        exteriorColor: '',
        interiorColor: '',
        customMakeModel: '',
        locateType: null,
        isDamaged: false,
        titleStatus: null,
        titleFiles: [],
        damageFiles: [],
        damageNote: '',
        source: null,
        wholesaler: null,
        customer: null,
        altExteriorColors: [],
        altInteriorColors: [],
        tags: [],
        notes: '',
        lastInventory: null,
        accessoryStatusHistory: [],
        damageHistory: [],
        serviceHistory: []
      }
    },
    /*
     * ⚠ The decode's free-text fallback, reached by three routes above, and the place the bad model
     * data was created. The decode returns the model GROUP - "812", "911", "296", "Giulia" - where the
     * catalogue holds the specific rows, and writing the group here saved a vehicle with no catalogue
     * link that drops out of model reporting. Garrett, 17 Sep, on the live Ferrari and Porsche
     * inventory: "Wow, it's every car."
     *
     * The model is now withheld when the catalogue can disambiguate the name; MakeModelSelector sees a
     * decode with no model and raises the choice prompt. The two decisions share one predicate on
     * purpose - see decodedModelNeedsChoice.
     */
    buildCustomMakeModel: function (make, productionYear, model, trim, style) {
      /*
       * ⚠ Guarded on what the record ALREADY holds, not only on the candidates. Edit Inventory can
       * re-decode at any time, and blanking a model the operator had chosen leaves them no way back -
       * the decode does not remember the value it replaced.
       */
      const recordHasModel = !!(this.selectedMakeModel &&
        (this.selectedMakeModel.modelId > 0 || this.selectedMakeModel.customModelName))
      const withholdModel = !recordHasModel &&
        decodedModelNeedsChoice(this.makes, make, model, productionYear)

      if (productionYear) {
        this.selectedMakeModel.customMakeModel = productionYear
      }
      if (make) {
        this.selectedMakeModel.customMakeModel += ' ' + make
        this.selectedMakeModel.customMakeName = make
      }
      if (model && !withholdModel) {
        this.selectedMakeModel.customMakeModel += ' ' + model
        this.selectedMakeModel.customModelName = model
      }
      if (trim) {
        this.selectedMakeModel.customMakeModel += ' ' + trim
      }
      if (style) {
        this.selectedMakeModel.customMakeModel += ' ' + style
      }
    },
    onGenerateVin: function (ev) {
      if (this.currentUser.id === 14018 || this.currentUser.id === 41041 || this.currentUser.id === 27027 || this.currentUser.id === 1) {
        this.onDecodeVin(true, !ev.ctrlKey, true)
      }
    },
    onDecodeVin: function (autoGenerate, internalMake, forceRun) {
      if (forceRun === false) {
        if (!this.isValid && !autoGenerate) {
          return
        }

        if (this.selectedVinNo.trim() === '' && !autoGenerate) {
          return
        }

        if (this.selectedMakeModel.decoded === true && this.selectedMakeModel.vinNo === this.selectedVinNo.trim()) {
          return
        }
      }

      this.resetDefaults()
      this.decodingVin = true
      this.selectedVinNo = this.selectedVinNo.trim()
      this.selectedMakeModel.vinNo = this.selectedVinNo
      let vinNoModel = {
        id: this.vehicleId,
        dealerId: this.currentDealer.id,
        vinNo: this.selectedVinNo,
        generate: (typeof autoGenerate === 'boolean') ? autoGenerate : false,
        brandId: internalMake ? 13 : null,
        getAutoCheck: this.getAutoCheck,
        getCarfax: false
      }
      if (this.ppaClosedCheck) {
        this.decodeVinPpa(autoGenerate, vinNoModel)
      } else {
        this.decodeVin(autoGenerate, vinNoModel)
      }
    },
    decodeVinPpa (autoGenerate, vinNoModel) {
      let that = this
      inventoryService.decodeVinPpa(vinNoModel).then(response => {
        if (response.errors) {
          that.decodingVin = false
          that.$emit('error', response.errors)
          return
        }
        that.selectedMakeModel.lookupResult = response.lookupResult
        if (response.lookupResult.valid && response.lookupResult.valid === true) {
          var make = response.lookupResult.make
          var productionYear = +response.lookupResult.productionYear
          var model = response.lookupResult.model
          var trim = response.lookupResult.trim
          var style = response.lookupResult.style
          if (autoGenerate && response.lookupResult.vin) {
            that.selectedVinNo = response.lookupResult.vin
            that.selectedMakeModel.vinNo = that.selectedVinNo
          }
          that.selectedMakeModel.decoded = true
          that.selectedMakeModel.trim = trim
          that.selectedMakeModel.style = style
          that.selectedMakeModel.productionYear = productionYear
          that.selectedMakeModel.vinUsageHistory = response.usageHistory || []
          if (response.lastInventory) {
            that.selectedMakeModel.lastInventory = response.lastInventory
            that.selectedMakeModel.accessoryStatusHistory = response.accessoryStatusHistory
            that.selectedMakeModel.damageHistory = response.damageHistory
            that.selectedMakeModel.serviceHistory = response.serviceHistory
            that.selectedMakeModel.exteriorColor = response.lastInventory.exteriorColor
            that.selectedMakeModel.interiorColor = response.lastInventory.interiorColor
          }
          var foundMake = make ? that.makes.find((x) => x.name.toUpperCase() === make.toUpperCase()) : null
          if (foundMake) {
            that.selectedMakeModel.makeId = foundMake.id
            that.selectedMakeModel.selectedMake = foundMake
            if (foundMake.versions && foundMake.versions.length > 0) {
              var foundProductionYearModel = findCatalogueModel(foundMake, model, productionYear)
              if (foundProductionYearModel) {
                that.selectedMakeModel.modelId = foundProductionYearModel.id
                that.selectedMakeModel.selectedModel = foundProductionYearModel
                that.selectedMakeModel.productionYear = foundProductionYearModel.productionYear
              } else {
                that.buildCustomMakeModel(make, productionYear, model, trim, style)
              }
            } else {
              that.buildCustomMakeModel(make, productionYear, model, trim, style)
            }
          } else {
            that.buildCustomMakeModel(make, productionYear, model, trim, style)
          }
        }
        that.decodingVin = false
        that.$emit('vin-resolved', that.selectedMakeModel)
        if (!that.hasCarfaxTesterRole && that.selectedMakeModel?.lookupResult) {
          that.getUpdatedCarfaxDealerReport()
        }
        if (response.existingPpaInventory) {
          that.$emit('existing-ppa', response.existingPpaInventory)
        }
      }).catch((error) => {
        console.log('error', error)
        that.decodingVin = false
        that.$emit('error', [error])
      })
    },
    decodeVin (autoGenerate, vinNoModel) {
      let that = this
      inventoryService.decode(vinNoModel).then(response => {
        if (response.errors) {
          that.decodingVin = false
          that.$emit('error', response.errors)
          return
        }
        that.selectedMakeModel.lookupResult = response.lookupResult
        if (response.lookupResult.valid && response.lookupResult.valid === true) {
          var make = response.lookupResult.make
          var productionYear = +response.lookupResult.productionYear
          var model = response.lookupResult.model
          var trim = response.lookupResult.trim
          var style = response.lookupResult.style
          if (autoGenerate && response.lookupResult.vin) {
            that.selectedVinNo = response.lookupResult.vin
            that.selectedMakeModel.vinNo = that.selectedVinNo
          }
          that.selectedMakeModel.decoded = true
          that.selectedMakeModel.trim = trim
          that.selectedMakeModel.style = style
          that.selectedMakeModel.productionYear = productionYear
          that.selectedMakeModel.vinUsageHistory = response.usageHistory || []
          if (response.lastInventory) {
            that.selectedMakeModel.lastInventory = response.lastInventory
            that.selectedMakeModel.accessoryStatusHistory = response.accessoryStatusHistory
            that.selectedMakeModel.damageHistory = response.damageHistory
            that.selectedMakeModel.serviceHistory = response.serviceHistory
            that.selectedMakeModel.exteriorColor = response.lastInventory.exteriorColor
            that.selectedMakeModel.interiorColor = response.lastInventory.interiorColor
          }
          if (make) {
            var foundMake = that.makes.find((x) => x.name.toUpperCase() === make.toUpperCase())
            if (foundMake) {
              that.selectedMakeModel.makeId = foundMake.id
              that.selectedMakeModel.selectedMake = foundMake
              if (foundMake.versions && foundMake.versions.length > 0) {
                var foundProductionYearModel = findCatalogueModel(foundMake, model, productionYear)
                if (foundProductionYearModel) {
                  that.selectedMakeModel.modelId = foundProductionYearModel.id
                  that.selectedMakeModel.selectedModel = foundProductionYearModel
                  that.selectedMakeModel.productionYear = foundProductionYearModel.productionYear
                } else {
                  that.buildCustomMakeModel(make, productionYear, model, trim, style)
                }
              } else {
                that.buildCustomMakeModel(make, productionYear, model, trim, style)
              }
            } else {
              that.buildCustomMakeModel(make, productionYear, model, trim, style)
            }
          }
        }

        that.decodingVin = false
        that.$emit('vin-resolved', that.selectedMakeModel)
        if (!that.hasCarfaxTesterRole && that.selectedMakeModel?.lookupResult) {
          that.getUpdatedCarfaxDealerReport()
        }
      }).catch((error) => {
        that.decodingVin = false
        that.$emit('error', [error])
      })
    },
    getVehicleCarFaxRating: function () {
      let userId = this.currentUser.id
      let that = this
      let vinNo = this.selectedVinNo
      if (this.decodingVin === false && vinNo !== '') {
        this.decodingVin = true

        appraisalService.extractCarfaxRatingFromDealerReport(userId, vinNo, false, 0).then(response => {
          that.decodingVin = false
          if (response.errors) {
            that.serverErrors = response.errors
            that.$emit('on-carfax-failed')
          } else {
            if (response.carfaxRating && that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = {
                ...response.carfaxRating
              }
            } else if (that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = null
            }
            that.$emit('carfax-resolved', that.selectedMakeModel)
          }
        }).catch((error) => {
          that.serverErrorMessage = error.message
          that.decodingVin = false
          that.$emit('on-carfax-failed')
        })
      }
    },
    getVehicleCarFaxRatingPurchaseIfRequired: function () {
      let userId = this.currentUser.id
      let that = this
      let vinNo = this.selectedVinNo
      if (this.decodingVin === false && vinNo !== '') {
        this.decodingVin = true

        appraisalService.extractCarfaxRatingFromDealerReport(userId, vinNo, true, 0).then(response => {
          that.decodingVin = false
          if (response.errors) {
            that.serverErrors = response.errors
            if (that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = null
            }
            that.$emit('on-carfax-failed')
          } else {
            if (response.carfaxRating && that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = {
                ...response.carfaxRating
              }
            } else if (that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = null
            }
            that.$emit('carfax-resolved', that.selectedMakeModel)
          }
        }).catch((error) => {
          that.serverErrorMessage = error.message
          that.decodingVin = false
          that.$emit('on-carfax-failed')
        })
      }
    },
    getUpdatedCarfaxDealerReport: function () {
      let that = this
      let vinNo = this.selectedVinNo
      let userId = this.currentUser.id
      if (this.decodingVin === false && vinNo !== '') {
        this.decodingVin = true
        appraisalService.getVehicleCarFaxUrlByVin(userId, vinNo).then(response => {
          that.decodingVin = false
          if (response.errors) {
            that.serverErrors = response.errors
            if (that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = null
            }
            that.$emit('on-carfax-failed')
          } else {
            if (response && that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = {
                ...response
              }
            } else if (that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = null
            }
            that.$emit('carfax-resolved', that.selectedMakeModel)
            if (that.selectedMakeModel.lookupResult && that.selectedMakeModel.lookupResult.carFaxRating) {
              that.extractCarfaxRatingFromDealerReportUrl()
            }
          }
        }).catch((error) => {
          that.$emit('on-carfax-failed')
          that.decodingVin = false
          that.serverErrorMessage = error.message
          if (that.selectedMakeModel?.lookupResult) {
            that.selectedMakeModel.lookupResult.carFaxRating = null
          }
          if (process.env.NODE_ENV !== 'production') {
            console.error('CarFax error:', error)
          }
          if (error.message === 'The requested report is not in your existing inventory of reports and will need to be purchased.') {
            that.$emit('on-carfax-payment-required')
          } else {
            that.$emit('on-carfax-failed')
            that.serverErrorMessage = error.message
            that.currentCarFaxRating = null
          }
        })
      } else {
        that.$emit('on-carfax-failed')
        that.decodingVin = false
        if (that.selectedMakeModel?.lookupResult) {
          that.selectedMakeModel.lookupResult.carFaxRating = null
        }
      }
    },
    extractCarfaxRatingFromDealerReportUrl: function () {
      let that = this
      let vinNo = this.selectedVinNo
      let userId = this.currentUser.id
      if (this.decodingVin === false && vinNo !== '') {
        appraisalService.extractCarfaxRatingFromDealerReportUrl(userId, that.selectedMakeModel.lookupResult.carFaxRating).then(response => {
          if (response.errors) {
            that.serverErrors = response.errors
          } else {
            if (response && that.selectedMakeModel?.lookupResult) {
              that.selectedMakeModel.lookupResult.carFaxRating = response.carfaxRating
            }
            that.$emit('carfax-resolved', that.selectedMakeModel)
          }
        }).catch((error) => {
        })
      } else {
      }
    },
    purchaseCarfaxReport: function () {
      this.isPaymentRequiredForReportActive = false
    },
    onCarfaxPurchaseRequested: function () {
      this.$emit('on-carfax-requested')
      this.isRefreshing = true
    },
    onCarfaxPurchaseFailed: function () {
      this.isRefreshing = false
      this.$emit('on-carfax-failed')
    },
    onCarfaxPurchased: function (carFaxRating, detailedHistory) {
      if (carFaxRating) {
        if (this.selectedMakeModel && this.selectedMakeModel.lookupResult) {
          //⚠ The purchase popup hands on the history its combined call already returned; carried the same
          //way as on the decode path so the mixin does not fetch it again.
          this.selectedMakeModel.lookupResult.carFaxRating = {
            ...carFaxRating,
            pendingDetailedHistory: detailedHistory || null
          }
        }
      } else if (this.selectedMakeModel && this.selectedMakeModel.lookupResult) {
        this.selectedMakeModel.lookupResult.carFaxRating = null
      }
      this.$emit('carfax-resolved', this.selectedMakeModel)
    },
    onCarfaxLoginRequired: function () {
      if (this.currentUserHasCarfaxSession === false) {
        this.showLoginModal = true
      } else if (!this.vinNo) {
        this.failedToast('Vin number required.')
      }
      this.$emit('on-carfax-failed')
      this.isRefreshing = false
    },
    onCarfaxPaymentModalClosed: function () {
      this.isPaymentRequiredForReportActive = false
    },
    requestCarfaxPurchase: function () {
      this.isPaymentRequiredForReportActive = true
    },
    getDetailedHistoryFromCarfaxDealerReport: function () {
      this.isRefreshing = true
      let that = this
      let carfaxRating = this.selectedMakeModel.lookupResult.carFaxRating
      let vinNo = this.vinNo
      let inventoryId = that.inventory ? that.inventory.id : 0
      if (carfaxRating && carfaxRating.tempBlobUrl && !this.hasCarfaxTesterRole) {
        appraisalService.extractDetailedHistoryFromCarfaxDealerReport(carfaxRating.tempBlobUrl, vinNo, inventoryId).then(response => {
          if (response.errors) {
            that.serverErrors = response.errors
            that.isRefreshing = false
          } else {
            that.selectedMakeModel.lookupResult.carFaxRating.vehiclePurchaseDate = response.detailedHistory.vehiclePurchaseDate

            //⚠ lastServiceDate, NOT lastVehicleOfferedForSaleDate. The two come from DISJOINT event sets
            //server-side - DeriveVehicleHistory filters on "Vehicle offered for sale" and on "Vehicle serviced"
            //into separate accumulators - and they have separate columns. This read the offered date into both,
            //so the CARFAX overview printed one date twice while the source and comments below it, which were
            //always correct, described a different event.
            that.selectedMakeModel.lookupResult.carFaxRating.lastServiceDate = response.detailedHistory.lastServiceDate
            that.selectedMakeModel.lookupResult.carFaxRating.lastServiceSource = response.detailedHistory.lastServiceSource
            that.selectedMakeModel.lookupResult.carFaxRating.lastServiceComments = response.detailedHistory.lastServiceComments
            
            that.selectedMakeModel.lookupResult.carFaxRating.lastVehicleOfferedForSaleDate = response.detailedHistory.lastVehicleOfferedForSaleDate
            that.selectedMakeModel.lookupResult.carFaxRating.lastVehicleOfferedForSaleSource = response.detailedHistory.lastVehicleOfferedForSaleSource
            
            that.selectedMakeModel.lookupResult.carFaxRating.vehicleImportedDate = response.detailedHistory.vehicleImportedDate
            that.selectedMakeModel.lookupResult.carFaxRating.vehicleImportedSource = response.detailedHistory.vehicleImportedSource
            that.selectedMakeModel.lookupResult.carFaxRating.vehicleImportedComments = response.detailedHistory.vehicleImportedComments

            that.selectedMakeModel.lookupResult.carFaxRating.vehicleRecallDate = response.detailedHistory.vehicleRecallDate
            that.selectedMakeModel.lookupResult.carFaxRating.vehicleRecallSource = response.detailedHistory.vehicleRecallSource
            that.selectedMakeModel.lookupResult.carFaxRating.vehicleRecallComments = response.detailedHistory.vehicleRecallComments

            that.selectedMakeModel.lookupResult.carFaxRating.damageReportData = []
            if (response.detailedHistory.carfaxDamageReportData && response.detailedHistory.carfaxDamageReportData.length > 0) {
              that.selectedMakeModel.lookupResult.carFaxRating.damageReportData.push(...response.detailedHistory.carfaxDamageReportData)
            }

            that.isRefreshing = false
            that.$emit('carfax-resolved', this.selectedMakeModel)
          }
        }).catch((error) => {
          that.serverErrorMessage = error.message
          that.isRefreshing = false
        })
      } else {
        this.isRefreshing = false
      }
    }
  },
  watch: {
    selectedVinNo: function (newValue, oldValue) {
      if (newValue) {
        this.$emit('input', newValue.toUpperCase())
      }
    },
    value: function (newValue, oldValue) {
      this.selectedVinNo = newValue
    }
  },
  beforeDestroy () {
    eventBus.$off('on-decode-vin', this.onDecodeVin)
  },
  mounted: function () {
    eventBus.$on('on-decode-vin', this.onDecodeVin)
    this.selectedVinNo = this.value || ''
  },
  created: function () {
    if (this.useValidator) {
      this.$validator = this.useValidator
    } else {
      this.$validator = this.$parent.$validator
    }
  }
}
</script>

<style scoped>

.is-valid {
  color:green !important;
  text-transform: uppercase;
}

.is-not-valid {
  color:red !important;
  text-transform: uppercase;
}
</style>
